/**
 * Target 2-min projection arrows (mockup's `showProjections` SVG block).
 * One dashed line per target, in trust-based colour, length = speed_kts * 2 / 60 nm.
 */
import type { GeoJSONSource, Map } from 'mapbox-gl';
import { useTargetsStore } from '@/lib/store/targets';
import { useUiStore } from '@/lib/store/ui';
import type { Target } from '@/lib/mqtt/schemas/targets';

const SRC = 'src-target-proj';
const LAYER = 'lyr-target-proj';

const M_PER_DEG_LAT = 111_320;
const mPerDegLng = (lat: number) => 111_320 * Math.cos((lat * Math.PI) / 180);

function project(t: Target): [number, number] {
  const distM = (t.speed_kts * 1852 / 60) * 2; // 2 minutes ahead
  const hRad = (t.heading * Math.PI) / 180;
  const dLat = (distM * Math.cos(hRad)) / M_PER_DEG_LAT;
  const dLng = (distM * Math.sin(hRad)) / mPerDegLng(t.lat);
  return [t.lng + dLng, t.lat + dLat];
}

export function ensureTargetProjectionLayer(map: Map) {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer(LAYER)) {
    map.addLayer({
      id: LAYER,
      type: 'line',
      source: SRC,
      paint: {
        'line-color': ['match', ['get', 'trust'], 'high', 'rgba(37,99,235,.55)', /* low */ 'rgba(249,115,22,.6)'],
        'line-width': 1.5,
        'line-dasharray': [5, 3],
      },
    });
  }
}

export function syncTargetProjectionLayer(map: Map) {
  const src = map.getSource(SRC) as GeoJSONSource | undefined;
  if (!src) return;
  const show = useUiStore.getState().showProjections;
  if (!show) {
    src.setData({ type: 'FeatureCollection', features: [] });
    return;
  }
  const targets = Object.values(useTargetsStore.getState().byId);
  const features: GeoJSON.Feature[] = targets.map((t) => {
    const [endLng, endLat] = project(t);
    return {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[t.lng, t.lat], [endLng, endLat]] },
      properties: { trust: t.trust, target_id: t.target_id },
    };
  });
  src.setData({ type: 'FeatureCollection', features });
}

export function subscribeTargetProjectionLayer(map: Map): () => void {
  ensureTargetProjectionLayer(map);
  syncTargetProjectionLayer(map);
  let rafId: number | null = null;
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => { rafId = null; syncTargetProjectionLayer(map); });
  };
  const unsubT = useTargetsStore.subscribe((s) => s.byId, schedule);
  const unsubShow = useUiStore.subscribe((s) => s.showProjections, schedule);
  return () => {
    unsubT();
    unsubShow();
  };
}
